import { useEffect, useRef, useState } from "react";
import { Loader2, X, ShieldCheck, Lock, ArrowRight } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const CASHFREE_SDK = "https://sdk.cashfree.com/js/v3/cashfree.js";
const CASHFREE_MODE = "production"; // toggle to "sandbox" for tests

// Singleton SDK loader
let _cfPromise = null;
function loadCashfree() {
  if (_cfPromise) return _cfPromise;
  _cfPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("ssr"));
    if (window.Cashfree) return resolve(window.Cashfree);
    const s = document.createElement("script");
    s.src = CASHFREE_SDK;
    s.async = true;
    s.onload = () => resolve(window.Cashfree);
    s.onerror = () => { _cfPromise = null; reject(new Error("sdk load failed")); };
    document.head.appendChild(s);
  });
  return _cfPromise;
}

/**
 * Embedded Cashfree drop-in checkout (modal overlay).
 * Mounts inside our domain — no redirect.
 */
export default function CashfreeCheckout({ open, onClose, onSuccess, defaultEmail = "", defaultName = "", defaultPhone = "" }) {
  const containerRef = useRef(null);
  const [stage, setStage] = useState("form"); // form | loading | drop | error | success
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [phone, setPhone] = useState(defaultPhone);
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState("");

  useEffect(() => { if (open) { setStage("form"); setError(""); } }, [open]);

  if (!open) return null;

  const startCheckout = async (e) => {
    e?.preventDefault();
    setError("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setError("Please enter a valid email."); return; }
    setStage("loading");

    try {
      // 1. Capture lead (best-effort)
      try {
        await fetch(`${API_BASE}/api/marketing/cart-abandon`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), name: name.trim(), phone: phone.trim(), country: "IN", currency: "INR" }),
        });
      } catch {}

      // 2. Create order on our backend
      const r = await fetch(`${API_BASE}/api/payments/cashfree/create-order`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), phone: phone.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Could not create order");
      }
      const order = await r.json();
      const sessionId = order.payment_session_id;
      if (!sessionId) throw new Error("Missing payment_session_id");
      setOrderId(order.order_id);

      // 3. Load + render Cashfree drop-in inside our container
      const Cashfree = await loadCashfree();
      const cf = Cashfree({ mode: CASHFREE_MODE });

      setStage("drop");
      // Wait for container to render
      await new Promise(r => setTimeout(r, 50));

      const checkoutOptions = {
        paymentSessionId: sessionId,
        redirectTarget: "_self", // ✅ embedded mount = on-domain
      };

      cf.checkout(checkoutOptions).then((result) => {
        if (result.error) {
          setStage("error");
          setError(result.error.message || "Payment failed.");
          return;
        }
        if (result.redirect) {
          // Cashfree handled the flow internally; webhook will fire shortly
          setStage("success");
          if (onSuccess) onSuccess({ order_id: order.order_id, email: email.trim(), name: name.trim() });
          // Auto-redirect to thank-you page (webhook will mint token; ThankYou.jsx polls for it)
          setTimeout(() => {
            window.location.href = `/?cf_order_id=${encodeURIComponent(order.order_id)}&email=${encodeURIComponent(email.trim())}&name=${encodeURIComponent(name.trim())}`;
          }, 600);
        }
        if (result.paymentDetails) {
          setStage("success");
          setTimeout(() => {
            window.location.href = `/?cf_order_id=${encodeURIComponent(order.order_id)}&email=${encodeURIComponent(email.trim())}&name=${encodeURIComponent(name.trim())}`;
          }, 600);
        }
      }).catch((e) => {
        setStage("error");
        setError(e?.message || "Checkout error.");
      });
    } catch (e) {
      setStage("error");
      setError(e?.message || "Could not start checkout.");
    }
  };

  return (
    <div data-testid="cashfree-modal" className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="cf-title">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md mx-auto sm:mx-4 max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl"
           style={{ background: "#0e0e12", border: "1px solid #2a2a35" }}>
        <div className="flex items-center justify-between p-4 border-b border-[#23232a] sticky top-0 bg-[#0e0e12]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "linear-gradient(135deg, #f5d06a 0%, #b78a1e 100%)" }}>
              <Lock className="w-4 h-4 text-black" />
            </div>
            <div>
              <div id="cf-title" className="font-display font-bold text-white text-[14px] leading-tight">Secure Cashfree Checkout</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mt-0.5">PCI-DSS · 256-bit SSL</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close checkout" data-testid="cashfree-close" className="text-[color:var(--ink-mute)] hover:text-white p-1.5 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5">
          {stage === "form" && (
            <form onSubmit={startCheckout} className="space-y-3.5">
              <div>
                <label htmlFor="cf-email" className="block font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-1">Email *</label>
                <input id="cf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="cashfree-email"
                       className="w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-11 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none text-[14px]" autoComplete="email" />
              </div>
              <div>
                <label htmlFor="cf-name" className="block font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-1">Name</label>
                <input id="cf-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="cashfree-name"
                       className="w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-11 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none text-[14px]" autoComplete="name" />
              </div>
              <div>
                <label htmlFor="cf-phone" className="block font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-1">Phone (for UPI / WhatsApp delivery)</label>
                <input id="cf-phone" type="tel" inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" data-testid="cashfree-phone"
                       className="w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-11 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none text-[14px]" autoComplete="tel" />
              </div>
              {error && <div data-testid="cashfree-error" className="text-[12.5px] text-red-400 bg-red-500/5 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}
              <button type="submit" data-testid="cashfree-submit"
                className="w-full btn-gold h-12 rounded-full inline-flex items-center justify-center gap-2 text-[14px] font-display">
                Continue to Payment <ArrowRight className="w-4 h-4" />
              </button>
              <div className="flex items-center justify-center gap-2 text-[11px] text-[color:var(--ink-mute)] pt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--gold)]" /> Cashfree Payments · UPI · Cards · Netbanking · Wallets
              </div>
            </form>
          )}

          {stage === "loading" && (
            <div className="py-12 flex flex-col items-center text-center">
              <Loader2 className="w-8 h-8 text-[color:var(--gold)] animate-spin mb-4" />
              <div className="font-display font-bold text-white">Preparing secure checkout…</div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)] mt-1.5">Creating order with Cashfree</div>
            </div>
          )}

          <div ref={containerRef} data-testid="cashfree-drop-container"
               style={{ minHeight: stage === "drop" ? "560px" : 0 }}
               className={stage === "drop" ? "block" : "hidden"} />

          {stage === "error" && (
            <div className="py-8 text-center">
              <div className="font-display font-bold text-red-400">Payment couldn't start</div>
              <div className="text-[13px] text-[color:var(--ink-dim)] mt-2">{error}</div>
              <button onClick={() => setStage("form")} className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 text-sm transition-colors">
                Try again
              </button>
            </div>
          )}

          {stage === "success" && (
            <div className="py-12 text-center">
              <div className="w-12 h-12 mx-auto rounded-full grid place-items-center mb-3" style={{ background: "rgba(0,255,136,.15)", border: "1px solid rgba(0,255,136,.45)" }}>
                <ShieldCheck className="w-6 h-6 text-[color:var(--green)]" />
              </div>
              <div className="font-display font-bold text-white">Payment received</div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)] mt-1.5">Redirecting to your license…</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
